import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Mail, Sparkles, ExternalLink, Inbox, Send } from "lucide-react";
import { useAuth } from "@/AuthContext";
import api, { formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ provider: null });
  const [leads, setLeads] = useState([]);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/provider/me").then(({ data }) => {
      setData(data);
      if (data.provider) setEdit({
        tagline: data.provider.tagline || "",
        summary: data.provider.summary || "",
        description: data.provider.description || "",
        contact_email: data.provider.contact_email || "",
        contact_phone: data.provider.contact_phone || "",
        website: data.provider.website || "",
        linkedin_url: data.provider.linkedin_url || "",
        logo_url: data.provider.logo_url || "",
      });
    });
    api.get("/provider/leads").then(({ data }) => setLeads(data.items || []));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: updated } = await api.patch(`/providers/${data.provider.id}`, edit);
      setData({ provider: updated });
      toast.success("Listing updated.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const p = data.provider;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Provider dashboard</div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Welcome, {user?.name}</h1>
        </div>
        {p && (
          <Link to={`/solutions/${p.slug}`} className="btn-secondary" data-testid="dash-view-public">
            View public profile <ExternalLink size={14}/>
          </Link>
        )}
      </div>

      {!p && (
        <div className="card-elevated p-12 text-center">
          <Building2 size={40} className="mx-auto text-slate-300"/>
          <div className="mt-3 font-display font-bold text-xl">No listing linked yet</div>
          <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
            Claim an existing listing or submit your company to get started.
          </p>
          <div className="mt-5 flex justify-center gap-3">
            <Link to="/solutions" className="btn-secondary">Browse directory</Link>
            <Link to="/submit-provider" className="btn-primary">Submit listing</Link>
          </div>
        </div>
      )}

      {p && (
        <div className="grid lg:grid-cols-[1fr_320px] gap-8">
          <div>
            <div className="card-elevated p-6 mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <Stat label="Tier" value={p.tier}/>
                <Stat label="Status" value={p.claim_status}/>
                <Stat label="Leads received" value={leads.length}/>
              </div>
            </div>

            <form onSubmit={save} className="card-elevated p-6 space-y-5" data-testid="provider-edit-form">
              <div className="font-display font-bold text-lg text-slate-900">Edit listing</div>
              <Field label="Tagline (one-line positioning)">
                <Input value={edit.tagline} onChange={(e)=>setEdit({...edit, tagline: e.target.value})} maxLength={200} data-testid="dash-tagline"/>
              </Field>
              <Field label="Summary (2–3 sentences)">
                <Textarea rows={3} value={edit.summary} onChange={(e)=>setEdit({...edit, summary: e.target.value})} data-testid="dash-summary"/>
              </Field>
              <Field label="Full description">
                <Textarea rows={6} value={edit.description} onChange={(e)=>setEdit({...edit, description: e.target.value})} data-testid="dash-description"/>
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Website"><Input value={edit.website} onChange={(e)=>setEdit({...edit, website: e.target.value})} data-testid="dash-website"/></Field>
                <Field label="Contact email"><Input type="email" value={edit.contact_email} onChange={(e)=>setEdit({...edit, contact_email: e.target.value})} data-testid="dash-email"/></Field>
                <Field label="Phone"><Input value={edit.contact_phone} onChange={(e)=>setEdit({...edit, contact_phone: e.target.value})} data-testid="dash-phone"/></Field>
                <Field label="LinkedIn URL"><Input value={edit.linkedin_url} onChange={(e)=>setEdit({...edit, linkedin_url: e.target.value})} data-testid="dash-linkedin"/></Field>
                <Field label="Logo URL"><Input value={edit.logo_url} onChange={(e)=>setEdit({...edit, logo_url: e.target.value})} data-testid="dash-logo"/></Field>
              </div>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="dash-save">{saving ? "Saving…" : "Save changes"}</button>
            </form>

            <div className="card-elevated p-6 mt-6">
              <div className="font-display font-bold text-lg text-slate-900 mb-3 flex items-center gap-2"><Inbox size={18}/> Received leads</div>
              {leads.length === 0 ? (
                <div className="text-sm text-slate-500">No leads yet. Upgrade to Verified or Featured for direct lead routing.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {leads.map((l) => (
                    <li key={l.id} className="py-3 flex items-start gap-3" data-testid={`lead-${l.id}`}>
                      <Mail size={16} className="text-slate-400 mt-1"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{l.name} — {l.company}</div>
                        <div className="text-xs text-slate-500">{l.email} · {l.country || "—"} · {new Date(l.created_at).toLocaleDateString()}</div>
                        <div className="text-sm text-slate-700 mt-1 line-clamp-2">{l.message || l.tracking_target}</div>
                      </div>
                      <span className="tag">{l.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <aside className="space-y-4">
            <div className="card-elevated p-6 bg-slate-900 text-white border-slate-900">
              <Sparkles size={20} className="text-orange-300"/>
              <div className="mt-3 font-display font-bold">Upgrade your visibility</div>
              <p className="mt-2 text-sm text-slate-300">Move to Verified ($197/mo) or Featured ($297/mo) for direct lead routing, priority placement and homepage exposure. Both paid tiers include a 14-day free trial.</p>
              <Link to="/pricing" className="mt-4 inline-flex items-center gap-2 bg-white text-slate-900 font-semibold px-4 py-2 rounded-md text-sm" data-testid="dash-upgrade">
                View tiers
              </Link>
            </div>
            <div className="card-elevated p-5">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Profile completeness</div>
              <Completeness provider={p}/>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display font-extrabold text-2xl text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function Completeness({ provider }) {
  const checks = [
    ["Logo", !!provider.logo_url],
    ["Tagline", !!provider.tagline],
    ["Description", (provider.description || "").length > 50],
    ["Website", !!provider.website],
    ["Categories", (provider.solution_categories || []).length > 0],
    ["Industries", (provider.industries || []).length > 0],
    ["Connectivity", (provider.connectivity || []).length > 0],
    ["Contact email", !!provider.contact_email],
  ];
  const done = checks.filter(([, ok]) => ok).length;
  const pct = Math.round((done / checks.length) * 100);
  return (
    <div>
      <div className="font-display font-extrabold text-2xl text-slate-900">{pct}%</div>
      <div className="h-2 bg-slate-100 rounded-full mt-2 overflow-hidden">
        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }}/>
      </div>
      <ul className="mt-3 space-y-1 text-xs">
        {checks.map(([label, ok]) => (
          <li key={label} className={ok ? "text-emerald-600" : "text-slate-400"}>
            {ok ? "✓" : "○"} {label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function UpgradeRequestForm({ providerId }) {
  const [tier, setTier] = useState("");
  const [message, setMessage] = useState("");
  const [trialRequested, setTrialRequested] = useState(true);
  const [recent, setRecent] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/provider/upgrade-requests").then(({ data }) => setRecent(data.items || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!tier) { toast.error("Choose a tier first."); return; }
    setSubmitting(true);
    try {
      await api.post("/provider/upgrade-requests", { requested_tier: tier, message, trial_requested: trialRequested });
      toast.success("Upgrade request submitted. Our team will be in touch.");
      const { data } = await api.get("/provider/upgrade-requests");
      setRecent(data.items || []);
      setMessage("");
      setTier("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const pending = recent.find((r) => ["New", "Reviewing"].includes(r.status));

  if (pending) {
    return (
      <div className="mt-4 rounded-md border border-white/20 bg-white/[0.05] p-3 text-xs text-slate-200" data-testid="dash-upgrade-pending">
        <div className="font-semibold text-white">Request pending review</div>
        <div className="mt-1">You requested <strong>{pending.requested_tier}</strong> on {new Date(pending.created_at).toLocaleDateString()}. Our team will respond shortly.</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-4 space-y-2" data-testid="dash-upgrade-form">
      <Select value={tier} onValueChange={setTier}>
        <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="dash-upgrade-tier"><SelectValue placeholder="Choose tier…"/></SelectTrigger>
        <SelectContent>
          <SelectItem value="Verified">Verified</SelectItem>
          <SelectItem value="Featured">Featured</SelectItem>
        </SelectContent>
      </Select>
      <Textarea
        rows={2}
        placeholder="Tell us about your goals (optional)"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
        data-testid="dash-upgrade-message"
      />
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={trialRequested}
          onChange={(e) => setTrialRequested(e.target.checked)}
          className="rounded border-white/30 bg-white/10"
          data-testid="dash-upgrade-trial"
        />
        Start 14-day free trial
      </label>
      <button type="submit" disabled={submitting}
        className="w-full bg-white text-slate-900 font-semibold px-4 py-2 rounded-md text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
        data-testid="dash-upgrade-submit">
        <Send size={14}/> {submitting ? "Sending…" : "Request upgrade"}
      </button>
    </form>
  );
}
